import { deriveId, newId, type CategoryId, type PayeeId, type PayeeRuleId } from "../ids";
import type { Payee, PayeeRule } from "../schemas";
import { CommandError, type DocWriter } from "../doc";
import { regexRuleProblem } from "../import/regexSafety";

export const startingBalancePayeeId = deriveId("payee", "builtin", "starting-balance");
export const reconciliationPayeeId = deriveId("payee", "builtin", "reconciliation-adjustment");

export function ensurePayee(w: DocWriter, id: PayeeId, name: string): Payee {
  const existing = w.get("payees", id);
  if (existing && !existing.deleted) {
    return existing;
  }
  return w.put("payees", id, { id, name, deleted: false });
}

export function findPayeeByName(w: DocWriter, name: string): Payee | null {
  const wanted = name.trim().toLowerCase();
  return w.list("payees").find((p) => p.name.trim().toLowerCase() === wanted) ?? null;
}

/** Returns the payee with this name (case-insensitive), creating it if needed. */
export function findOrCreatePayee(w: DocWriter, name: string, id?: PayeeId): Payee {
  const trimmed = name.trim();
  if (trimmed === "") {
    throw new CommandError("A payee needs a name");
  }
  return (
    findPayeeByName(w, trimmed) ??
    w.put("payees", id ?? newId("payee"), { name: trimmed, deleted: false })
  );
}

/** A rename to a name another payee already has (ignoring case); the UI offers to merge. */
export class PayeeNameTakenError extends CommandError {
  constructor(
    readonly existingPayeeId: PayeeId,
    existingName: string,
  ) {
    super(`There is already a payee called ${existingName}. Merge them instead.`);
    this.name = "PayeeNameTaken";
  }
}

export function renamePayee(w: DocWriter, id: PayeeId, name: string): Payee {
  if (!w.get("payees", id)) {
    throw new CommandError("Payee not found");
  }
  const taken = findPayeeByName(w, name);
  if (taken && taken.id !== id) {
    throw new PayeeNameTakenError(taken.id, taken.name);
  }
  return w.put("payees", id, { name: name.trim() });
}

/** Moves transactions, split lines, rules and memories to `targetId`, then deletes the sources. */
export function mergePayees(w: DocWriter, sourceIds: PayeeId[], targetId: PayeeId): void {
  if (!w.get("payees", targetId)) {
    throw new CommandError("Payee not found");
  }
  const sources = new Set<string>(sourceIds.filter((id) => id !== targetId));
  for (const t of w.list("transactions")) {
    const splits = t.amountAndSplits.splits;
    const splitsChange = splits.some((s) => s.payeeId && sources.has(s.payeeId));
    if ((t.payeeId && sources.has(t.payeeId)) || splitsChange) {
      w.put("transactions", t.id, {
        payeeId: t.payeeId && sources.has(t.payeeId) ? targetId : t.payeeId,
        amountAndSplits: splitsChange
          ? {
              amount: t.amountAndSplits.amount,
              splits: splits.map((s) =>
                s.payeeId && sources.has(s.payeeId) ? { ...s, payeeId: targetId } : s,
              ),
            }
          : t.amountAndSplits,
      });
    }
  }
  for (const r of w.list("payeeRules")) {
    if (sources.has(r.payeeId)) {
      w.put("payeeRules", r.id, { payeeId: targetId });
    }
  }
  for (const m of w.list("memories")) {
    if (m.payeeId && sources.has(m.payeeId)) {
      w.put("memories", m.id, { payeeId: targetId });
    }
  }
  for (const s of w.list("scheduled")) {
    if (s.payeeId && sources.has(s.payeeId)) {
      w.put("scheduled", s.id, { payeeId: targetId });
    }
  }
  for (const id of sources) {
    w.remove("payees", id);
  }
}

export type PayeeRuleInput = Omit<PayeeRule, "id" | "deleted" | "priority"> & {
  priority?: number;
};

export function upsertPayeeRule(w: DocWriter, id: PayeeRuleId, input: PayeeRuleInput): PayeeRule {
  if (input.match === "regex") {
    const problem = regexRuleProblem(input.pattern);
    if (problem !== null) {
      throw new CommandError(problem);
    }
  }
  if (
    input.amountMin !== undefined &&
    input.amountMax !== undefined &&
    input.amountMin > input.amountMax
  ) {
    throw new CommandError("The rule's minimum amount is above its maximum");
  }
  return w.put("payeeRules", id, { priority: 0, ...input, deleted: false });
}

export function deletePayeeRule(w: DocWriter, id: PayeeRuleId): void {
  w.remove("payeeRules", id);
}

/** The category most recently used with a payee (derived, not stored: SPEC §4). */
export function lastCategoryForPayee(
  transactions: Iterable<{
    payeeId?: PayeeId;
    categoryId?: CategoryId;
    date: string;
    deleted: boolean;
    transfer?: unknown;
  }>,
  payeeId: PayeeId,
): CategoryId | null {
  let best: { date: string; categoryId: CategoryId } | null = null;
  for (const t of transactions) {
    if (
      !t.deleted &&
      t.payeeId === payeeId &&
      t.categoryId &&
      !t.transfer &&
      (!best || t.date >= best.date)
    ) {
      best = { date: t.date, categoryId: t.categoryId };
    }
  }
  return best?.categoryId ?? null;
}
